import React from 'react';
import { Link } from 'react-router-dom';
import { Package, Clock, Sparkles, Truck, CreditCard, ShoppingBag, HelpCircle } from 'lucide-react';

const ReturnsPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h1 className="text-4xl font-bold text-[#2d3c5d] mb-4">
            Política de Cambios y Devoluciones
          </h1>
          <p className="text-gray-600">
            En Deco Home Sin Rival queremos que disfrutes tu compra. Si necesitás hacer un cambio o devolución, te explicamos cómo funciona el proceso y cuáles son las condiciones generales.
          </p>
        </div>

        {/* Contenido */}
        <div className="space-y-6">
          {/* Productos con cambio */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Package className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">🔁 Productos con cambio</h2>
                <div className="text-gray-700 space-y-2">
                  <p>
                    Todos nuestros productos pueden cambiarse por gusto personal, falla o error de envío, <strong>excepto los artículos de outlet o segunda selección y la vajilla</strong>, que no tienen cambio ni devolución.
                  </p>
                  <p className="text-red-600 font-semibold">
                    ⚠️ No se aceptan cambios ni reclamos de productos usados o dañados.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Plazo */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">🕐 Plazo para gestionar un cambio o devolución</h2>
                <div className="text-gray-700 space-y-2">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Tenés hasta <strong>10 días corridos</strong> desde la recepción del pedido para solicitar un cambio o devolución.</li>
                    <li>En caso de arrepentimiento de compra, también contás con <strong>10 días</strong> desde la fecha de compra online.</li>
                    <li className="text-red-600 font-semibold">Pasado ese plazo, no se aceptarán solicitudes.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Condiciones generales */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">✨ Condiciones generales</h2>
                <div className="text-gray-700 space-y-2">
                  <ul className="list-disc list-inside space-y-1">
                    <li>El producto debe estar <strong>sin uso, en perfecto estado y con su empaque original</strong>.</li>
                    <li>No se aceptarán productos con manchas, roturas, lavados o sin etiquetas.</li>
                    <li>Las solicitudes que no cumplan estas condiciones serán rechazadas.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Costos de envío */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Truck className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">🚚 Costos de envío</h2>
                <div className="text-gray-700 space-y-3">
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                    <p>
                      <strong>Si el cambio es por gusto personal o error del cliente:</strong><br />
                      El envío corre por cuenta del comprador.
                    </p>
                  </div>
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                    <p>
                      <strong>Si el cambio es por falla o error nuestro:</strong><br />
                      Deco Home Sin Rival cubre el envío.
                    </p>
                  </div>
                  <p className="text-sm">
                    ⚠️ No nos responsabilizamos por daños ocasionados por el correo. En esos casos, solo se reintegra el valor del producto, no el costo del envío.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Reintegros */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">💳 Reintegros</h2>
                <div className="text-gray-700 space-y-3">
                  <p>Los reintegros se realizan solo en los siguientes casos:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Producto con falla comprobada sin stock disponible para reemplazo.</li>
                    <li>Pedidos no retirados en sucursal OCA (5 días hábiles) y devueltos al remitente, sin posibilidad de reenvío.</li>
                  </ul>

                  <p className="font-semibold mt-4">En esos casos:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Si pagaste con tarjeta, la devolución se reflejará en el siguiente resumen.</li>
                    <li>Si pagaste por transferencia, el reintegro se hará por el mismo medio.</li>
                    <li>Si queda un saldo a favor, se emitirá un voucher sin vencimiento válido para futuras compras online.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Cómo solicitar */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">🛍️ Cómo solicitar un cambio o devolución</h2>
                <div className="text-gray-700 space-y-3">
                  <p>
                    Escribinos a{' '}
                    <a href="mailto:info@decohomesinrival.com.ar" className="text-[#2d3c5d] hover:underline font-semibold">
                      info@decohomesinrival.com.ar
                    </a>{' '}
                    indicando tu número de orden, el motivo del cambio o devolución y adjuntá fotos si es necesario.
                  </p>
                  <p className="font-semibold">
                    Te responderemos dentro de las 72 horas hábiles con los pasos a seguir.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Pedidos OCA */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-[#2d3c5d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Package className="w-6 h-6 text-[#2d3c5d]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2d3c5d] mb-3">📦 Pedidos con retiro en sucursal OCA</h2>
                <div className="text-gray-700 space-y-3">
                  <p>Tenés <strong>5 días hábiles</strong> para retirar tu pedido desde que llega a la sucursal.</p>
                  <p>Si no lo retirás, el paquete volverá a nuestro depósito. Podés:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Solicitar un reenvío (abonando nuevamente el envío), o</li>
                    <li>Pedir un reintegro, descontando el costo del envío original.</li>
                  </ul>
                  <p className="text-sm">
                    ⏱️ El regreso del paquete puede demorar entre 25 y 30 días hábiles, según los tiempos de OCA.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ¿Tenés dudas? */}
          <section className="bg-[#2d3c5d] rounded-lg p-8 text-white">
            <div className="flex items-start gap-4">
              <HelpCircle className="w-8 h-8 flex-shrink-0" style={{ color: '#eddacb' }} />
              <div>
                <h2 className="text-2xl font-bold mb-4">¿Tenés dudas?</h2>
                <p className="mb-4" style={{ color: '#eddacb' }}>
                  Podés iniciar tu gestión desde el botón de ayuda disponible en el sitio o escribirnos a{' '}
                  <a href="mailto:info@decohomesinrival.com.ar" className="hover:underline font-semibold">
                    info@decohomesinrival.com.ar
                  </a>
                </p>
                <a
                  href="mailto:info@decohomesinrival.com.ar"
                  className="inline-flex items-center px-6 py-3 rounded-lg font-semibold transition"
                  style={{
                    backgroundColor: '#eddacb',
                    color: '#2d3c5d',
                  }}
                >
                  Contactar ahora
                </a>
              </div>
            </div>
          </section>

          {/* Botón volver */}
          <div className="pt-6">
            <Link
              to="/"
              className="inline-block px-6 py-3 rounded-lg font-semibold text-white transition"
              style={{ backgroundColor: '#2d3c5d' }}
            >
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnsPolicy;
